/* Copyright (c) 2024 Xperi. All Rights Reserved.  */

import { ActionGroup, ActionKernelType, ActionType } from 'client-code/actions';
import { PreviewType } from 'client-code/previews';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { TRecordOverlayParams } from '../../../core/types/TRecordOverlayParams';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import {
    DescriptionType,
    IDescriptionEntry,
    IOverlayDialog,
    IOverlayOptions,
} from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { UniqueIdentifier } from '../../../core/utils/UniqueIdentifier';
import { TOfferPreviewCustomData } from '../../../core/types';
import { Channel } from '../../../core/entities/Channel';
import { OverlayTypes } from '../OverlayTypes';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import {
    APPLICATION_FEATURE_AREA,
    LEFT_QUOTE,
    MILLISECONDS_IN_A_SECOND,
    RIGHT_QUOTE,
} from '../../../config';
import { startVideoPlayback } from '../../videoplayer/videoplayer.reducers';
import { Log, NFRLoggings, VuLogger } from '../../../core/logging';
import { showChannelOptionsOverlay } from '../channeloptionsoverlay/channeloptionsoverlay.reducers';
import { SocuDataModelImpl } from '../../../core/socu/impls';
import { PlaybackContentType } from '../../../core/socu/enums/PlaybackContentType';
import { AppUtils } from '../../../core/AppUtils';
import { prepareVodResumeStartOverOverlayFromGuide } from '../vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import { OfferPreviewFormatter } from '../../../views/Guide/PreviewPane/OfferPreviewFormatter';
import { VideoManager } from '../../../core/videoplayer';
import { ServicesClientImpl } from '../../../core/impls/ServicesClientImpl';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { showSocuErrorOverlay } from '../socuerroroverlay/socuerroroverlay.reducres';
import { BrandingUiUtility } from '../../../core/utils/BrandingUiUtility';
import { AppError } from '../../../core/error';
import { NFREvents } from '../../../core/enums/NFREvents';
import { DeviceFeatures } from '../../../core/device';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { guideHideSpinner, guideShowSpinner } from '../../guide/guide.reducers';

// eslint-disable-next-line no-unused-vars, no-shadow
enum RecordOverlayOptionsType {
    // eslint-disable-next-line no-unused-vars
    SEE_CHANNEL_OPTIONS,
    // eslint-disable-next-line no-unused-vars
    WATCH_FROM_LIVE,
    // eslint-disable-next-line no-unused-vars
    WATCH_FROM_SOCU,
    // eslint-disable-next-line no-unused-vars
    WATCH_FROM_SOCU_NOT_AVAILABLE,
}

const GLOBAL_MOUSE_TRAP = 'HideRecordOverlay';

const TAG = 'RecordOverlay';

const getDescriptionFirstLine = (offerPreview: TOfferPreviewCustomData) => {
    return offerPreview.contentPreview?.description || '';
};

const getDescriptionSecondLine = (offerPreviewFormatter: OfferPreviewFormatter) => {
    const videoResolution = offerPreviewFormatter.getCcVideoResolution();
    const categoryAndFirstAired = offerPreviewFormatter.getCategoryAndFirstAired();
    return `${videoResolution}${categoryAndFirstAired}`;
};

const getDescriptionThirdLine = (offerPreviewFormatter: OfferPreviewFormatter) => {
    let descrpThirdLine = '';
    const contentAiringDateTime = offerPreviewFormatter.getContentAiringDateTime();
    const internalRatingsDiv = offerPreviewFormatter.getInternalRatings().map((rating) => rating);
    const starRating = offerPreviewFormatter.hasStarRating()
        ? offerPreviewFormatter.getStarRating()
        : '';
    descrpThirdLine += contentAiringDateTime;
    descrpThirdLine +=
        contentAiringDateTime.length && (internalRatingsDiv.length || starRating) ? ' |  ' : '';
    descrpThirdLine += `${internalRatingsDiv}${starRating}`;
    return descrpThirdLine;
};

const getFullDescription = (offerPreview: TOfferPreviewCustomData) => {
    const offerPreviewFormatter = new OfferPreviewFormatter(offerPreview);
    const descrpFirstLine = getDescriptionFirstLine(offerPreview);
    const descrpSecondLine = getDescriptionSecondLine(offerPreviewFormatter);
    const descrpThirdLine = getDescriptionThirdLine(offerPreviewFormatter);

    let finalDescription = descrpFirstLine;
    if (finalDescription.trim() && descrpSecondLine.trim()) {
        finalDescription += '\n';
    }
    finalDescription += descrpSecondLine;
    if (finalDescription.trim() && descrpThirdLine.trim()) {
        finalDescription += '\n';
    }
    finalDescription += descrpThirdLine;
    if (!finalDescription.trim()) {
        finalDescription = Locale.getString(LocaleKeys.OVERLAY_DESCRIPTION_NOT_AVAILABLE);
    }
    return finalDescription;
};

const startLiveTvFromRecordOverlay = (channel: Channel) => (dispatch: Function) => {
    const socuModelImpl = SocuDataModelImpl.getInstance();
    if (socuModelImpl.isSocu()) {
        socuModelImpl.resetSocuData();
    } else {
        socuModelImpl.resetNewSocuData();
    }
    if (
        channel.channelNumber !== VideoManager.getInstance().getPlayable()?.channel?.channelNumber
    ) {
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.CHANNEL_CHANGE);
    }
    dispatch(startVideoPlayback(channel));
};

export const showRecordOverlay =
    (requirements: TRecordOverlayParams) => async (dispatch: Function, getState: Function) => {
        dispatch(guideShowSpinner());
        const { channel, guideCell, showMetaData } = requirements;
        const networkCallId = UniqueIdentifier.getNextActionsNetworkCallId();
        const socuModelImpl = SocuDataModelImpl.getInstance();
        if (socuModelImpl.getPlaybackContentType() !== PlaybackContentType.LIVE) {
            VideoManager.getInstance().updateLastViewedPosition(APPLICATION_FEATURE_AREA.GUIDE);
        }
        const services = new ServicesClientImpl();
        const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
        const { isCatchup, isStartover } = guideCell;
        const isSocu = isCatchup || isStartover;
        const currentTime = Date.now();
        const programEndTime =
            guideCell.startTime.getTime() + guideCell.duration * MILLISECONDS_IN_A_SECOND;
        const currentlyAiring =
            guideCell.startTime.getTime() <= currentTime && currentTime < programEndTime;
        const alreadyAired = currentTime > programEndTime;
        const cuAvailabilityDuration =
            (channel.policyInfo?.find((policy) => policy?.socuRules)?.socuRules
                ?.cuAvailabilityDurationSeconds || 0) * MILLISECONDS_IN_A_SECOND;
        const availableForCatchup = Date.now() - programEndTime <= cuAvailabilityDuration;
        const followsSocuDisplayRules =
            DeviceFeatures.isSocuEnabled() &&
            isSocu &&
            (currentlyAiring || (alreadyAired && availableForCatchup));
        let watchFromProviderAction;
        if (followsSocuDisplayRules) {
            try {
                NFRLoggings.log(NFREvents.START_LOADING_ACTIONS_LIST);
                const { actionsNetworkCallId, actionList } = await socuModelImpl.fetchActionList({
                    channelNumber: channel.channelNumber!,
                    groups: [ActionGroup.Watch],
                    offerId: guideCell.offerId,
                    screenType: APPLICATION_FEATURE_AREA.GUIDE,
                    actionsNetworkCallId: networkCallId,
                });
                if (actionsNetworkCallId === UniqueIdentifier.getActionsNetworkCallId()) {
                    watchFromProviderAction = actionList.actions.find((action) => {
                        return (
                            action.actionType === ActionType.WatchFromProviderAction &&
                            action.kernel?.actionKernelType ===
                                ActionKernelType.WatchFromSocuActionKernel
                        );
                    });
                    NFRLoggings.log(NFREvents.FINISH_LOADING_ACTIONS_LIST);
                } else {
                    Log.i(TAG, 'Network call id mismatched.');
                    return;
                }
            } catch (error) {
                (error as AppError).setDescription(
                    BrandingUiUtility.formatDescription((error as AppError).getDescription(), {
                        CATCHUP_NAME: currentlyAiring
                            ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                            : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP),
                        ERROR_CODE: (error as AppError).getCode(),
                    }),
                );
                Log.e(TAG, 'error while showRecordOverlay: ', error);
                Diagnostics.error(TAG, 'socu provider not found', error);
                NFRLoggings.log(NFREvents.FAILURE_LOADING_ACTIONS_LIST, error as any);
                socuModelImpl.setOfferActionError(error as AppError);
            }
        }

        const { offerPreview }: { offerPreview: TOfferPreviewCustomData } = getState().gridGuide;

        let title: string = offerPreview.contentPreview?.title || '';
        let subtitle: string | { text: string; icon: string } = '';
        const isAdult = offerPreview.contentPreview?.isAdult;

        if (isAdult) {
            title = Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
            subtitle = Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN);
        } else if (offerPreview.contentPreview?.previewType === PreviewType.EpisodePreview) {
            const { episodeNumber, seasonNumber, episodeTitle } = offerPreview.contentPreview;

            const episodeTitleText = episodeTitle
                ? `${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}`
                : '';

            const episodeText: string = episodeNumber ? `E${episodeNumber}` : '';
            const seasonText: string = seasonNumber ? `S${seasonNumber} ` : '';
            subtitle = (seasonText + episodeText).length ? `${seasonText}${episodeText} ` : '';

            subtitle =
                !subtitle.length && offerPreview.contentPreview?.firstAiredDate
                    ? AppUtils.strftime(offerPreview.contentPreview?.firstAiredDate, '%M/%D/%y')
                    : subtitle;

            subtitle =
                subtitle.length && episodeTitleText?.length
                    ? `${subtitle} ${episodeTitleText}`
                    : subtitle;
        } else if (offerPreview.contentPreview?.previewType === PreviewType.MoviePreview) {
            const movieYearText = offerPreview.contentPreview.movieYear || '';
            title = `${title}${movieYearText}`;
        } else if (!offerPreview || !title) {
            title = Locale.getString(LocaleKeys.OVERLAY_TITLE_NOT_AVAILABLE_TEXT);
        }

        if (offerPreview.contentPreview?.isNew) {
            subtitle = {
                text: subtitle,
                icon: newIcon?.imageUrl,
            };
        }

        const overlayOptions: Array<IOverlayOptions> = [];

        const description: IDescriptionEntry[] = [];
        if (showMetaData) {
            let descriptionText = getFullDescription(offerPreview);

            if (isAdult) {
                descriptionText = Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN);
            }

            description.push({
                text: descriptionText,
                descriptionType: DescriptionType.PROGRAM_DESCRIPTION,
            });
        }

        const showWatchFromProviderAction =
            watchFromProviderAction && channel.isSubscribed && followsSocuDisplayRules;
        if (showWatchFromProviderAction) {
            overlayOptions.push({
                hrTop: showMetaData,
                text: `${Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_NOW_FROM_TEXT)} ${
                    watchFromProviderAction.provider.displayName
                }`,
                value: RecordOverlayOptionsType.WATCH_FROM_SOCU,
                displayValue: {
                    type: 'image',
                    // TODO: remove this hardcoding,
                    // TODO: also update dimension once confirmed by UX
                    value: [
                        `https://i-stg.tivo.com/images/${watchFromProviderAction.provider.providerId}/partnerSourceLogo_49x49`,
                    ],
                },
            });
        }

        const showWatchFromProviderActionNotAvailable =
            !watchFromProviderAction && followsSocuDisplayRules && channel.isSubscribed;
        if (showWatchFromProviderActionNotAvailable) {
            overlayOptions.push({
                hrTop: showMetaData,
                text: `${
                    currentlyAiring
                        ? services.getBrandingUiInfoForKey(BrandingUiInfoKeys.START_OVER)
                        : services.getBrandingUiInfoForKey(BrandingUiInfoKeys.CATCH_UP)
                } ${Locale.getString(LocaleKeys.NOT_AVAILABLE)}`,
                value: RecordOverlayOptionsType.WATCH_FROM_SOCU_NOT_AVAILABLE,
            });
        }

        if (currentlyAiring) {
            overlayOptions.push({
                hrTop: !showWatchFromProviderAction && showMetaData,
                text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_WATCH_LIVE_TEXT),
                value: RecordOverlayOptionsType.WATCH_FROM_LIVE,
            });
        }

        const showNotAvailableToWatch =
            showMetaData &&
            !currentlyAiring &&
            !showWatchFromProviderAction &&
            !showWatchFromProviderActionNotAvailable;
        if (showNotAvailableToWatch) {
            description.push({
                descriptionType: DescriptionType.PROGRAM_DESCRIPTION,
                hrTop: true,
                text: Locale.getString(LocaleKeys.OVERLAY_SHOW_NOT_AVAILABLE_TO_WATCH_TEXT),
            });
        }

        overlayOptions.push({
            text: Locale.getString(LocaleKeys.OVERLAY_OPTIONS_SEE_CHANNEL_OPTIONS_TEXT),
            value: RecordOverlayOptionsType.SEE_CHANNEL_OPTIONS,
            hrTop: !!(
                currentlyAiring ||
                showWatchFromProviderAction ||
                showNotAvailableToWatch ||
                showWatchFromProviderActionNotAvailable
            ),
        });
        const overlayProps: IOverlayDialog = {
            description,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: GLOBAL_MOUSE_TRAP,
            options: overlayOptions,
            overlayType: OverlayTypes.RECORD,
            subtitle,
            title,
        };
        if (NetworkManager.isNetworkDisconnected()) {
            Log.i(TAG, 'Internet disconnected');
            return;
        }
        dispatch(guideHideSpinner());
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS));
        dispatch(showOverlayDialog(overlayProps));
    };

export const onKeyBackRecordOverlay = () => (dispatch: Function) => {
    SocuDataModelImpl.getInstance().resetNewSocuData();
    dispatch(hideOverlayDialog());
};

export const onKeyOkRecordOverlay = () => (dispatch: Function, getState: Function) => {
    const { gridGuide, guideHighlight, overlayHighlight, overlayDialog } = getState();

    const { channels } = gridGuide;
    const { verticalPosition } = guideHighlight;
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const channel: Channel = channels[verticalPosition];
    const selectedOption = options[overlayHighlightVerticalPosition];
    const socuModelImpl = SocuDataModelImpl.getInstance();

    dispatch(hideOverlayDialog());
    switch (selectedOption.value) {
        case RecordOverlayOptionsType.WATCH_FROM_LIVE:
            dispatch(startLiveTvFromRecordOverlay(channel));
            break;
        case RecordOverlayOptionsType.WATCH_FROM_SOCU:
            dispatch(prepareVodResumeStartOverOverlayFromGuide(channel));
            break;
        case RecordOverlayOptionsType.WATCH_FROM_SOCU_NOT_AVAILABLE:
            dispatch(showSocuErrorOverlay(socuModelImpl.getOfferActionError()));
            break;
        case RecordOverlayOptionsType.SEE_CHANNEL_OPTIONS:
            socuModelImpl.resetNewSocuData();
            dispatch(showChannelOptionsOverlay());
            break;
        default:
            socuModelImpl.resetNewSocuData();
            Log.e(
                TAG,
                'Unknown option selected in record overlay',
                options[overlayHighlightVerticalPosition],
            );
    }
};

export const onKeyPlayRecordOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayHighlight, overlayDialog } = getState();
    const { verticalPosition: overlayHighlightVerticalPosition } = overlayHighlight;
    const { options } = overlayDialog.uiProperties;
    const selectedOption = options[overlayHighlightVerticalPosition];

    if (selectedOption.value === RecordOverlayOptionsType.SEE_CHANNEL_OPTIONS) return;

    dispatch(onKeyOkRecordOverlay());
};

export const onMouseClickRecordOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }

    dispatch(onKeyOkRecordOverlay());
};
